// Meshy API クライアント — 3D キャラ自動生成（計画書§16.11・API 仕様は https://docs.meshy.ai）。
// すべて非同期タスク型: POST で作成 → GET /:id をポーリング（PENDING/IN_PROGRESS → SUCCEEDED/FAILED/CANCELED）。
// キーは利用者のブラウザ（API設定画面の localStorage）からリクエストヘッダで都度受領（メモリのみ・ファイル保存禁止。環境変数は CLI 都度指定用）。
import { readFileSync, writeFileSync, mkdirSync } from 'node:fs';
import path from 'node:path';
import { CONFIG_DIR, REPO_ROOT, containedPath } from './repo-paths.mjs';
import { readJson } from './json-utils.mjs';
import { getRuntimeKey } from './runtime-keys.mjs';

let cachedConfig = null;

export function meshyConfig() {
    if (!cachedConfig) cachedConfig = readJson(path.join(CONFIG_DIR, 'meshy.json'));
    return cachedConfig;
}

/**
 * Meshy API キーを解決する（値はログに出さない・利用者が自分で発行したキーのみ）。
 * 優先順: ①ランタイムストア（ブラウザ localStorage → リクエストヘッダで都度受領）②環境変数（CLI 用・都度指定）。
 * ファイルからは読まない（機密のファイル保存は禁止・2026-07-07 計画書§16.4）。
 */
export function resolveMeshyKey() {
    const runtime = getRuntimeKey('meshy');
    if (runtime) return { key: runtime, source: 'ブラウザ（API設定画面・localStorage）' };
    const cfg = meshyConfig();
    const fromEnv = cfg.keyEnv ? process.env[cfg.keyEnv] : null;
    if (fromEnv && fromEnv.trim() !== '') return { key: fromEnv.trim(), source: `環境変数 ${cfg.keyEnv}（CLI 用・都度指定）` };
    return { key: null, source: null };
}

export class MeshyError extends Error {
    constructor(message, { code = 'meshy_error', detail = null } = {}) {
        super(message);
        this.code = code;
        this.detail = detail;
    }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function requireKey() {
    const { key } = resolveMeshyKey();
    if (!key) {
        throw new MeshyError(
            'Meshy API キーが未設定です。ツールの「API設定」画面から、ご自身で発行したキー（https://app.meshy.ai/settings/api）を登録してください（キーはお使いのブラウザにのみ保存されます）',
            { code: 'meshy_key_missing' },
        );
    }
    return key;
}

async function meshyFetch(method, apiPath, body = null) {
    const key = requireKey();
    const url = `${meshyConfig().baseUrl}${apiPath}`;
    const res = await fetch(url, {
        method,
        headers: {
            authorization: `Bearer ${key}`,
            ...(body ? { 'content-type': 'application/json' } : {}),
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
        signal: AbortSignal.timeout(120000), // 単発の HTTP 呼び出し。生成待ちは pollTask 側で管理
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
        throw new MeshyError(
            `Meshy API エラー (HTTP ${res.status}): ${data?.message ?? JSON.stringify(data)?.slice(0, 200) ?? '(本文なし)'}`,
            { code: `meshy_http_${res.status}`, detail: data },
        );
    }
    return data;
}

/** タスク作成。応答の { result: "<taskId>" } から taskId を返す。 */
export async function createTask(apiPath, body) {
    const data = await meshyFetch('POST', apiPath, body);
    const taskId = data?.result ?? data?.id;
    if (!taskId) throw new MeshyError(`Meshy タスク ID を取得できません: ${JSON.stringify(data)?.slice(0, 200)}`, { detail: data });
    return String(taskId);
}

/**
 * タスク完了までポーリングする（工程タイムアウト既定 20 分・進捗はコールバックへ）。
 * @returns SUCCEEDED したタスクオブジェクト（model_urls / result / consumed_credits などを含む）
 */
export async function pollTask(apiPath, taskId, { onLog = () => {}, label = '' } = {}) {
    const cfg = meshyConfig();
    const timeoutMs = cfg.stageTimeoutMs ?? 1200000;
    const intervalMs = cfg.pollIntervalMs ?? 8000;
    const pollRetries = cfg.pollRetries ?? 3;
    const startedAt = Date.now();
    let lastProgress = -1;
    let getFailures = 0;
    for (;;) {
        if (Date.now() - startedAt > timeoutMs) {
            throw new MeshyError(
                `${label} がタイムアウトしました（${Math.round(timeoutMs / 60000)}分。Meshy 側でタスク ${taskId} は継続している可能性があります）`,
                { code: 'meshy_timeout' },
            );
        }
        // 20 分ポーリング（約 150 回の GET）の 1 回が 429/5xx/ネットワーク断で失敗しただけで
        // 工程全体を失敗（＝再実行で二重課金）にしない。恒久エラー（401/404 等）だけ即 throw する
        let task;
        try {
            task = await meshyFetch('GET', `${apiPath}/${taskId}`);
            getFailures = 0;
        } catch (e) {
            const status = Number(String(e?.code ?? '').replace('meshy_http_', ''));
            const transient = !(e instanceof MeshyError) || e.code === 'meshy_http_429' || (Number.isFinite(status) && status >= 500);
            getFailures += 1;
            if (!transient || getFailures > pollRetries) throw e;
            onLog(`    … ${label} の状態取得に失敗（${getFailures}/${pollRetries}）。${Math.round(intervalMs / 1000)}秒後に再試行します`);
            await sleep(intervalMs);
            continue;
        }
        const status = task?.status;
        const progress = Number(task?.progress ?? 0);
        if (progress !== lastProgress && (progress % 20 === 0 || status !== 'IN_PROGRESS')) {
            onLog(`    … ${label} ${status} ${progress}%（${Math.round((Date.now() - startedAt) / 1000)}秒経過）`);
            lastProgress = progress;
        }
        if (status === 'SUCCEEDED') return task;
        if (status === 'FAILED' || status === 'CANCELED') {
            const reason = task?.task_error?.message ?? task?.error ?? '(理由不明)';
            throw new MeshyError(`${label} が失敗しました（${status}）: ${reason}`, { code: 'meshy_task_failed', detail: task });
        }
        await sleep(intervalMs);
    }
}

/** プロジェクト内のキャスト画像を base64 データ URI にする（Meshy の image_url / image_style_url 用）。 */
export function imageFileToDataUri(absPath) {
    const buf = readFileSync(absPath);
    const ext = path.extname(absPath).toLowerCase();
    const mime = ext === '.png' ? 'image/png' : ext === '.webp' ? 'image/webp' : 'image/jpeg';
    return `data:${mime};base64,${buf.toString('base64')}`;
}

/**
 * GLB取得リクエストを実行環境別に組み立てる。
 * Node版はMeshy CDNへ直接、ブラウザ完結版はCORS制約を避けるため同一オリジンの
 * 許可ホスト限定プロキシへPOSTする。署名付きURLをクエリ文字列やログへ残さない。
 */
export function createAssetDownloadRequest(fileUrl) {
    const isBrowser = typeof window !== 'undefined' && typeof window.document !== 'undefined';
    if (!isBrowser) return { url: fileUrl, init: {} };
    const proxyPath = meshyConfig().download?.browserProxyPath;
    if (typeof proxyPath !== 'string' || !proxyPath.startsWith('/')) {
        throw new MeshyError('GLB取得プロキシの設定が不正です', { code: 'meshy_download_proxy_config' });
    }
    return {
        url: proxyPath,
        init: {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ url: fileUrl }),
        },
    };
}

/** Meshy配布URLからバイナリを取得する（ブラウザ版は同一オリジンプロキシ経由）。 */
export async function fetchAssetBytes(fileUrl) {
    const request = createAssetDownloadRequest(fileUrl);
    const res = await fetch(request.url, { ...request.init, signal: AbortSignal.timeout(300000) });
    if (!res.ok) {
        const detail = (await res.text().catch(() => '')).replace(/\s+/g, ' ').trim().slice(0, 200);
        throw new MeshyError(`GLB のダウンロードに失敗 (HTTP ${res.status})${detail ? `: ${detail}` : ''}`);
    }
    const buf = Buffer.from(await res.arrayBuffer());
    if (buf.length === 0) throw new MeshyError('GLB のダウンロード結果が空です');
    return buf;
}

/**
 * Meshy が返す URL から GLB を取得し、リポジトリの assets/ 配下（サンドボックス済み）へ保存する。
 * @param {string} assetsPath 'assets/models/...' 形式の保存先
 * @param {(buf: Uint8Array) => (unknown|Promise<unknown>)} [opts.validate] 保存前のバイナリ検証。throw時は保存しない
 * @returns {Promise<{path: string, bytes: number, validation?: unknown}>}
 */
export async function downloadToAssets(fileUrl, assetsPath, { onLog = () => {}, validate = null } = {}) {
    const buf = await fetchAssetBytes(fileUrl);
    return writeBytesToAssets(buf, assetsPath, { onLog, validate });
}

/** 検証済みバイナリを assets/ 配下の計算済みパスへ保存する。 */
export async function writeBytesToAssets(bytes, assetsPath, { onLog = () => {}, validate = null } = {}) {
    const rel = String(assetsPath).replace(/^assets\//, '');
    const abs = containedPath(path.join(REPO_ROOT, 'assets'), rel); // assets/ 外への書込を拒否
    const buf = Buffer.from(bytes);
    const validation = validate ? await validate(buf) : undefined;
    mkdirSync(path.dirname(abs), { recursive: true });
    writeFileSync(abs, buf);
    onLog(`    ✔ 保存: ${assetsPath}（${Math.round(buf.length / 1024)}KB）`);
    return { path: assetsPath, bytes: buf.length, ...(validation === undefined ? {} : { validation }) };
}
